import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import { getUserNotifications as getUserNotificationsApi } from "@/api/user";
import {
  deleteNotificationById as deleteNotificationByIdApi,
  readNotifications as readNotificationsApi,
  acceptOfferNotifications as acceptOfferNotificationsApi,
  declineOfferNotifications as declineOfferNotificationsApi,
  acceptInterviewNotifications as acceptInterviewNotificationsApi,
  declineInterviewNotifications as declineInterviewNotificationsApi,
} from "@/api/misc";
import { getOrganizationNotifications as getOrganizationNotificationsApi } from "@/api/organization";
import { declineApplyRequest } from "@/api/job";
import { acceptReportRequestById, declineReportRequestById } from "@/api/backgroundCheck";
import { type NotificationItem, type NotificationsResponse } from "@/types/notification";

interface NotificationState {
  data: NotificationsResponse;
  isFetchingData: boolean;
}

const initialState: NotificationState = {
  data: {
    next_cursor: null,
    notifications: [],
    unread_count: 0,
  },
  isFetchingData: false,
};

export const getUserNotifications = createAsyncThunk(
  "notification/getUserNotifications",
  async ({ userId, cursor }: { userId?: string; cursor?: string }) => {
    const notifications = await getUserNotificationsApi({ userId }, { cursor });
    return notifications;
  },
);

export const getOrganizationNotifications = createAsyncThunk(
  "notification/getOrganizationNotifications",
  async ({ organizationId, cursor }: { organizationId: string; cursor?: string }) => {
    const notifications = await getOrganizationNotificationsApi({ organizationId }, { cursor });
    return notifications;
  },
);

export const readNotifications = createAsyncThunk(
  "notification/readNotifications",
  async ({ userId = null }: { userId?: string }) => {
    const notifications = await readNotificationsApi(userId);
    return notifications;
  },
);

export const declineJobRequestNotificationsById = createAsyncThunk(
  "notification/declineJobRequestNotificationsById",
  async ({
    jobId = null,
    applyRequestId = null,
    notificationId,
  }: {
    jobId?: string;
    applyRequestId?: string;
    notificationId: string;
  }) => {
    await declineApplyRequest(jobId, applyRequestId);
    return notificationId;
  },
);

export const acceptOfferNotificationsById = createAsyncThunk(
  "notification/acceptOfferNotificationsById",
  async ({
    jobId = null,
    offerId = null,
    notificationId,
  }: {
    jobId?: string;
    offerId?: string;
    notificationId: string;
  }) => {
    await acceptOfferNotificationsApi(jobId, offerId);
    return notificationId;
  },
);

export const declineOfferNotificationsById = createAsyncThunk(
  "notification/declineOfferNotificationsById",
  async ({
    jobId = null,
    offerId = null,
    notificationId,
  }: {
    jobId?: string;
    offerId?: string;
    notificationId: string;
  }) => {
    await declineOfferNotificationsApi(jobId, offerId);
    return notificationId;
  },
);

export const acceptInterviewNotificationsById = createAsyncThunk(
  "notification/acceptInterviewNotificationsById",
  async ({
    organizationId = null,
    meetingId = null,
    notificationId,
  }: {
    organizationId?: string;
    meetingId?: string;
    notificationId: string;
  }) => {
    await acceptInterviewNotificationsApi(organizationId, meetingId);
    return notificationId;
  },
);

export const declineInterviewNotificationsById = createAsyncThunk(
  "notification/declineInterviewNotificationsById",
  async ({
    organizationId = null,
    meetingId = null,
    notificationId,
  }: {
    organizationId?: string;
    meetingId?: string;
    notificationId: string;
  }) => {
    await declineInterviewNotificationsApi(organizationId, meetingId);
    return notificationId;
  },
);

export const shareReportRequest = createAsyncThunk(
  "notification/shareReportRequest",
  async (notificationId: string, { rejectWithValue }) => {
    try {
      await acceptReportRequestById(notificationId);
      return notificationId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const declineReportRequest = createAsyncThunk(
  "notification/declineReportRequest",
  async (notificationId: string, { rejectWithValue }) => {
    try {
      await declineReportRequestById(notificationId);
      return notificationId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteNotificationById = createAsyncThunk(
  "notification/deleteNotificationById",
  async ({
    userId = null,
    organizationId = null,
    notificationId,
  }: {
    userId?: string;
    organizationId?: string;
    notificationId: string;
  }) => {
    await deleteNotificationByIdApi(organizationId, userId, notificationId);
    return notificationId;
  },
);

export const clearNotifications = createAsyncThunk(
  "notification/clearNotifications",
  async (_, { dispatch }) => {
    dispatch(notificationSlice.actions.resetNotifications());
  },
);

const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    resetNotifications(state) {
      state.data = initialState.data;
      state.isFetchingData = initialState.isFetchingData;
    },
    addNewNotification(state, action: PayloadAction<NotificationItem>) {
      const newNotification = action.payload;
      const existingIndex = state.data.notifications.findIndex(
        (notification) => notification.id === newNotification.id,
      );

      if (existingIndex === -1) {
        state.data.notifications.unshift(newNotification);

        if (!newNotification.read) {
          state.data.unread_count += 1;
        }
      }
    },
    markNotificationAsRead(state, action: PayloadAction<string>) {
      const notificationId = action.payload;
      const notificationIndex = state.data.notifications.findIndex(
        (notification) => notification.id === notificationId,
      );

      if (notificationIndex !== -1 && !state.data.notifications[notificationIndex].read) {
        state.data.notifications[notificationIndex].read = true;
        state.data.unread_count = Math.max(0, state.data.unread_count - 1);
      }
    },
    updateUnreadCount(state, action: PayloadAction<number>) {
      state.data.unread_count = action.payload;
    },
    markAllNotificationsAsRead(state) {
      state.data.notifications.forEach((notification) => {
        notification.read = true;
      });

      state.data.unread_count = 0;
    },
    updateNotificationData(
      state,
      action: PayloadAction<{ id: string; data: Record<string, any> }>,
    ) {
      const { id, data } = action.payload;
      const notification = state.data.notifications.find((n) => n.id === id);
      if (notification) {
        for (const [key, value] of Object.entries(data)) {
          notification.data[key] =
            typeof value === "object" && value !== null && !Array.isArray(value)
              ? { ...notification.data[key], ...value }
              : value;
        }
      }
    },
    markApplyRequestAsApplied(state, action: PayloadAction<string>) {
      const applyRequestId = action.payload;

      state.data.notifications.forEach((notification) => {
        const matchesApplyRequest =
          notification.type === "apply_request" &&
          (notification?.data?.apply_request?.id === applyRequestId ||
            notification?.data?.apply_request_id === applyRequestId);

        if (matchesApplyRequest && notification.data?.apply_request) {
          notification.data.apply_request.status = "applied";
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteNotificationById.fulfilled, (state, action: PayloadAction<string>) => {
        const deletedNotificationId = action.payload;
        state.data.notifications = state.data.notifications.filter(
          (notification) => notification.id !== deletedNotificationId,
        );
      })
      .addCase(
        declineJobRequestNotificationsById.fulfilled,
        (state, action: PayloadAction<string>) => {
          const notificationId = action.payload;
          const notification = state.data.notifications.find(
            (notification) => notification.id === notificationId,
          );
          if (notification && notification.data) {
            notification.data.apply_request.status = "declined";
          }
        },
      )
      .addCase(
        acceptInterviewNotificationsById.fulfilled,
        (state, action: PayloadAction<string>) => {
          const notificationId = action.payload;
          const notification = state.data.notifications.find(
            (notification) => notification.id === notificationId,
          );
          if (notification && notification.data) {
            notification.data.meeting.status = "accepted";
          }
        },
      )
      .addCase(
        declineInterviewNotificationsById.fulfilled,
        (state, action: PayloadAction<string>) => {
          const notificationId = action.payload;
          const notification = state.data.notifications.find(
            (notification) => notification.id === notificationId,
          );
          if (notification && notification.data) {
            notification.data.meeting.status = "rejected";
          }
        },
      )
      .addCase(acceptOfferNotificationsById.fulfilled, (state, action: PayloadAction<string>) => {
        const notificationId = action.payload;
        const notification = state.data.notifications.find(
          (notification) => notification.id === notificationId,
        );
        if (notification && notification.data) {
          notification.data.offer.status = "accepted";
        }
      })
      .addCase(declineOfferNotificationsById.fulfilled, (state, action: PayloadAction<string>) => {
        const notificationId = action.payload;
        const notification = state.data.notifications.find(
          (notification) => notification.id === notificationId,
        );
        if (notification && notification.data) {
          notification.data.offer.status = "rejected";
        }
      })
      .addCase(getUserNotifications.pending, (state) => {
        state.isFetchingData = true;
      })
      .addCase(getUserNotifications.fulfilled, (state, action: PayloadAction<any>) => {
        state.data = {
          ...action.payload,
          notifications: [
            ...state.data.notifications.filter(
              (notification) =>
                !action.payload.notifications.some(
                  (newNotification) => newNotification.id === notification.id,
                ),
            ),
            ...action.payload.notifications,
          ],
        };

        state.isFetchingData = false;
      })
      .addCase(getOrganizationNotifications.pending, (state) => {
        state.isFetchingData = true;
      })
      .addCase(getOrganizationNotifications.fulfilled, (state, action: PayloadAction<any>) => {
        state.data = {
          ...action.payload,
          notifications: [
            ...state.data.notifications.filter(
              (notification) =>
                !action.payload.notifications.some(
                  (newNotification) => newNotification.id === notification.id,
                ),
            ),
            ...action.payload.notifications,
          ],
        };

        state.isFetchingData = false;
      })
      .addCase(getUserNotifications.rejected, (state) => {
        state.isFetchingData = false;
      })
      .addCase(getOrganizationNotifications.rejected, (state) => {
        state.isFetchingData = false;
      })
      .addCase(readNotifications.fulfilled, (state) => {
        state.data.notifications.forEach((notification) => {
          notification.read = true;
        });

        state.data.unread_count = 0;
      })
      .addCase(shareReportRequest.fulfilled, (state, action: PayloadAction<string>) => {
        const notificationId = action.payload;
        const notification = state.data.notifications.find(
          (notification) => notification?.data?.bc_report_request?.id === notificationId,
        );
        if (notification && notification.data) {
          notification.data.bc_report_request.status = "shared";
          state.data.notifications = [...state.data.notifications];
        }
      })
      .addCase(shareReportRequest.rejected, (state, action) => {
        console.error("Failed to share report request:", action.payload);
      })
      .addCase(declineReportRequest.fulfilled, (state, action: PayloadAction<string>) => {
        const notificationId = action.payload;
        const notification = state.data.notifications.find(
          (notification) => notification?.data?.bc_report_request?.id === notificationId,
        );
        if (notification && notification.data) {
          notification.data.bc_report_request.status = "declined";
          state.data.notifications = [...state.data.notifications];
        }
      })
      .addCase(declineReportRequest.rejected, (state, action) => {
        console.error("Failed to decline report request:", action.payload);
      });
  },
});

export const {
  resetNotifications,
  addNewNotification,
  markNotificationAsRead,
  updateUnreadCount,
  markAllNotificationsAsRead,
  updateNotificationData,
  markApplyRequestAsApplied,
} = notificationSlice.actions;
export default notificationSlice.reducer;
